import { CountryCode, Variants, OldVariants, ProductCodeKey, BoxVariants } from '@dayetopia/library';
import { PAD_STACK_COUNTS, PAD_STACK_PRODUCT_IDS } from '@constants';
import { BoxItem, BoxState, PadSizeType, PadsBoxStackState, Prices } from '@contracts';

export function getAllPadTypes(): PadSizeType[] {
  return Object.values(PadSizeType);
}

export function areVariantsContainingOldPads(variants: Variants | OldVariants) {
  const allOldPadsKeys = getAllPadTypes();

  if (Array.isArray(variants)) {
    return variants.some((variant) => allOldPadsKeys.includes(variant.id as PadSizeType));
  }

  return Object.keys(variants).some((key) => allOldPadsKeys.includes(key as PadSizeType));
}

export function getPadVariantKeys(country: CountryCode) {
  const isStackBox = isPadsStackBox(country);
  const variantPl = isStackBox ? PAD_STACK_PRODUCT_IDS[PadSizeType.LINER] : PadSizeType.LINER;
  const variantPr = isStackBox ? PAD_STACK_PRODUCT_IDS[PadSizeType.REGULAR] : PadSizeType.REGULAR;
  const variantPn = isStackBox ? PAD_STACK_PRODUCT_IDS[PadSizeType.SUPER] : PadSizeType.SUPER;
  const variantPnp = isStackBox ? PAD_STACK_PRODUCT_IDS[PadSizeType.SUPER_PLUS] : PadSizeType.SUPER_PLUS;

  return {
    [PadSizeType.LINER]: variantPl,
    [PadSizeType.REGULAR]: variantPr,
    [PadSizeType.SUPER]: variantPn,
    [PadSizeType.SUPER_PLUS]: variantPnp
  };
}

export function getPadVariantCounts(country: CountryCode) {
  const isStackBox = isPadsStackBox(country);
  const variantPl = isStackBox ? PAD_STACK_PRODUCT_IDS[PadSizeType.LINER] : PadSizeType.LINER;
  const variantPr = isStackBox ? PAD_STACK_PRODUCT_IDS[PadSizeType.REGULAR] : PadSizeType.REGULAR;
  const variantPn = isStackBox ? PAD_STACK_PRODUCT_IDS[PadSizeType.SUPER] : PadSizeType.SUPER;
  const variantPnp = isStackBox ? PAD_STACK_PRODUCT_IDS[PadSizeType.SUPER_PLUS] : PadSizeType.SUPER_PLUS;

  return {
    [PadSizeType.LINER]: variantPl,
    [PadSizeType.REGULAR]: variantPr,
    [PadSizeType.SUPER]: variantPn,
    [PadSizeType.SUPER_PLUS]: variantPnp
  };
}

export function getPadTypeTextByType(type: PadSizeType) {
  const padSizeTypeTexts = getPadSizeTypeTexts();

  return padSizeTypeTexts[type];
}

export function getPadTypeLongTextByType(type: PadSizeType) {
  const padSizeTypeTexts = getPadSizeTypeLongTexts();

  return padSizeTypeTexts[type];
}

export function getPadTypeShortDescriptionByType(type: PadSizeType) {
  const padSizeTypeTexts = getPadSizeTypeShortDescriptions();

  return padSizeTypeTexts[type];
}

export function getLinerVariantKey(country: CountryCode) {
  const isStackBox = isPadsStackBox(country);

  return isStackBox ? PAD_STACK_PRODUCT_IDS[PadSizeType.LINER] : PadSizeType.LINER;
}

export function getRegularVariantKey(country: CountryCode) {
  const isStackBox = isPadsStackBox(country);

  return isStackBox ? PAD_STACK_PRODUCT_IDS[PadSizeType.REGULAR] : PadSizeType.REGULAR;
}

export function getSuperVariantKey(country: CountryCode) {
  const isStackBox = isPadsStackBox(country);

  return isStackBox ? PAD_STACK_PRODUCT_IDS[PadSizeType.SUPER] : PadSizeType.SUPER;
}

export function getSuperPlusVariantKey(country: CountryCode) {
  const isStackBox = isPadsStackBox(country);

  return isStackBox ? PAD_STACK_PRODUCT_IDS[PadSizeType.SUPER_PLUS] : PadSizeType.SUPER_PLUS;
}

export function getPadStackPriceFromPrices(prices: Prices, padSize: PadSizeType) {
  switch (padSize) {
    case PadSizeType.LINER:
      return prices.linerPadPrice;
    case PadSizeType.REGULAR:
      return prices.regularPadPrice;
    case PadSizeType.SUPER:
      return prices.nightPadPrice;
    case PadSizeType.SUPER_PLUS:
      return prices.nightPlusPadPrice;
    default:
      return -1;
  }
}

export function getPadStackPriceTotalFromPrices(prices: Prices, padsBoxStackState: PadsBoxStackState) {
  return getAllPadTypes().reduce((acc, padSize) => {
    return acc + getPadStackPriceFromPrices(prices, padSize) * padsBoxStackState[padSize];
  }, 0);
}

export function getPadSizeTypeTexts() {
  return {
    [PadSizeType.LINER]: 'components.liners-box',
    [PadSizeType.REGULAR]: 'components.regular-box',
    [PadSizeType.SUPER]: 'components.super-box',
    [PadSizeType.SUPER_PLUS]: 'components.super-plus-box'
  };
}

export function getPadSizeTypeLongTexts() {
  return {
    [PadSizeType.LINER]: 'components.liners',
    [PadSizeType.REGULAR]: 'components.regular',
    [PadSizeType.SUPER]: 'components.super',
    [PadSizeType.SUPER_PLUS]: 'components.super-plus-long'
  };
}

export function getPadSizeTypeShortDescriptions() {
  return {
    [PadSizeType.LINER]: 'components.liners-short-description',
    [PadSizeType.REGULAR]: 'components.regular-short-description',
    [PadSizeType.SUPER]: 'components.super-short-description',
    [PadSizeType.SUPER_PLUS]: 'components.super-plus-short-description'
  };
}

export function isBoxStatePadsStackBox(boxState: BoxState) {
  return !(boxState as BoxState)?.boxSize;
}

export function isPadsAndStackBox(productCode: ProductCodeKey, country: CountryCode) {
  return productCode === ProductCodeKey.Pads && isPadsStackBox(country);
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function isPadsStackBox(_country: CountryCode) {
  return true;
}

export function convertPadsStackBoxItems(boxItems: BoxItem[]) {
  return boxItems.map((item) => ({
    ...item,
    count: item.count * PAD_STACK_COUNTS[item?.key as keyof typeof PAD_STACK_COUNTS],
    price: (item.price || 0) / PAD_STACK_COUNTS[item?.key as keyof typeof PAD_STACK_COUNTS]
  }));
}

/**
 * Gets the current total count of pads based on the country's product type.
 * Different countries use different pad product systems:
 * - Stack box countries (e.g., GB): Use pre-packaged quantities where the value is the actual pad count
 * - Legacy countries: Use individual pad type selections that may need multiplication
 */
export const getCurrentPadsCount = (variants: Variants | BoxVariants, country: CountryCode) => {
  const isStackBox = isPadsStackBox(country);

  if (isStackBox) {
    return getCurrentStackPadsCount(variants);
  }

  return getCurrentLegacyPadsCount(variants);
};

export const getCurrentLegacyPadsCount = (variants: Variants | BoxVariants) => {
  if (!variants) return 0;
  const allPadTypes = getAllPadTypes();
  const padIds = [...allPadTypes];
  const variantsArray = Object.entries(variants);
  const pads = Object.fromEntries(variantsArray.filter(([key]) => padIds.some((padId) => padId === key)));
  const padsCount = calculatePadsCount(pads);

  return padsCount;
};

export const getCurrentStackPadsCount = (variants: Variants | BoxVariants) => {
  if (!variants) return 0;
  const allPadTypes = getAllPadTypes();
  const padIds = allPadTypes.map((pad) => PAD_STACK_PRODUCT_IDS[pad]);
  const variantsArray = Object.entries(variants);
  const pads = Object.fromEntries(variantsArray.filter(([key]) => padIds.some((padId) => padId === key)));

  // BUSINESS LOGIC: For stack box products (e.g., pads_box_24l, pads_box_12_regular),
  // the value in boxVariants represents the ACTUAL COUNT of pads, not the number of boxes.
  // This is different from legacy pad products where multiplication might be needed.
  // Example: { 'pads_box_24l': 24 } means 24 individual liner pads, not 24 boxes.
  let totalCount = 0;
  // eslint-disable-next-line no-restricted-syntax
  for (const [, count] of Object.entries(pads)) {
    totalCount += count;
  }

  return totalCount;
};

/**
 * Calculates the total count of pads based on the provided pads configuration.
 *
 * @param {Record<string, number>} pads - An object where keys are pad types and values are their respective counts.
 * @returns {number} The total count of pads.
 */
export function calculatePadsCount(pads: Record<string, number>): number {
  return (
    Number(
      Object.entries(pads).length > 0
        ? Object.entries(pads).reduce((total, [key, count]: any) => {
            const padCount = pads[PAD_STACK_PRODUCT_IDS[key as PadSizeType]] ? count * PAD_STACK_COUNTS[key as PadSizeType] : count;
            return total + padCount;
          }, 0)
        : 0
    ) ?? 0
  );
}
